import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import "./index.css";

type Operator = "+" | "-" | "×" | "÷" | null;

export function App() {
  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operator, setOperator] = useState<Operator>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);

  const inputDigit = (digit: string) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === "0" ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay("0.");
      setWaitingForOperand(false);
      return;
    }
    if (!display.includes(".")) {
      setDisplay(display + ".");
    }
  };

  const calculate = (left: number, right: number, op: Operator): number => {
    switch (op) {
      case "+": return left + right;
      case "-": return left - right;
      case "×": return left * right;
      case "÷": return right !== 0 ? left / right : NaN;
      default: return right;
    }
  };

  const handleOperator = (nextOperator: Operator) => {
    const currentValue = parseFloat(display);

    if (previousValue !== null && !waitingForOperand) {
      const result = calculate(previousValue, currentValue, operator);
      const resultStr = Number.isNaN(result) ? "Error" : String(parseFloat(result.toPrecision(12)));
      setDisplay(resultStr);
      setPreviousValue(result);
    } else {
      setPreviousValue(currentValue);
    }

    setOperator(nextOperator);
    setWaitingForOperand(true);
  };

  const handleEquals = () => {
    if (previousValue === null || operator === null) return;

    const currentValue = parseFloat(display);
    const result = calculate(previousValue, currentValue, operator);
    const resultStr = Number.isNaN(result) ? "Error" : String(parseFloat(result.toPrecision(12)));

    setDisplay(resultStr);
    setPreviousValue(null);
    setOperator(null);
    setWaitingForOperand(true);
  };

  const handleClear = () => {
    setDisplay("0");
    setPreviousValue(null);
    setOperator(null);
    setWaitingForOperand(false);
  };

  const handleToggleSign = () => {
    if (display !== "0") {
      setDisplay(display.startsWith("-") ? display.slice(1) : "-" + display);
    }
  };

  const handlePercent = () => {
    const value = parseFloat(display);
    setDisplay(String(parseFloat((value / 100).toPrecision(12))));
  };

  const formatDisplay = (value: string) => {
    if (value === "Error") return value;
    if (value.includes(".") && value.endsWith(".")) return value;
    const num = parseFloat(value);
    if (Number.isNaN(num)) return "Error";
    if (value.includes(".")) {
      const [int, dec] = value.split(".");
      return parseFloat(int).toLocaleString("en-US") + "." + dec;
    }
    return num.toLocaleString("en-US");
  };

  const displayFontSize = display.length > 12 ? "text-2xl" : display.length > 8 ? "text-3xl" : "text-5xl";

  const OperatorButton = ({ op, label }: { op: Operator; label: string }) => (
    <Button
      onClick={() => handleOperator(op)}
      className={`h-16 text-xl rounded-full font-medium transition-colors ${
        operator === op && waitingForOperand
          ? "bg-white text-orange-500 hover:bg-white/90"
          : "bg-orange-500 text-white hover:bg-orange-400"
      }`}
    >
      {label}
    </Button>
  );

  return (
    <div className="flex items-center justify-center min-h-screen">
      <Card className="w-[320px] bg-black border-none shadow-2xl rounded-3xl overflow-hidden">
        <CardContent className="p-4 pt-4">
          {/* Display */}
          <div className="h-24 flex items-end justify-end px-2 mb-2">
            <span className={`${displayFontSize} font-light text-white truncate`}>
              {formatDisplay(display)}
            </span>
          </div>

          {/* Button Grid */}
          <div className="grid grid-cols-4 gap-2">
            {/* Row 1 */}
            <Button onClick={handleClear} className="h-16 text-xl rounded-full bg-neutral-400 text-black hover:bg-neutral-300 font-medium">
              {previousValue !== null || display !== "0" ? "C" : "AC"}
            </Button>
            <Button onClick={handleToggleSign} className="h-16 text-xl rounded-full bg-neutral-400 text-black hover:bg-neutral-300 font-medium">
              +/−
            </Button>
            <Button onClick={handlePercent} className="h-16 text-xl rounded-full bg-neutral-400 text-black hover:bg-neutral-300 font-medium">
              %
            </Button>
            <OperatorButton op="÷" label="÷" />

            {/* Row 2 */}
            <Button onClick={() => inputDigit("7")} className="h-16 text-xl rounded-full bg-neutral-700 text-white hover:bg-neutral-600 font-medium">7</Button>
            <Button onClick={() => inputDigit("8")} className="h-16 text-xl rounded-full bg-neutral-700 text-white hover:bg-neutral-600 font-medium">8</Button>
            <Button onClick={() => inputDigit("9")} className="h-16 text-xl rounded-full bg-neutral-700 text-white hover:bg-neutral-600 font-medium">9</Button>
            <OperatorButton op="×" label="×" />

            {/* Row 3 */}
            <Button onClick={() => inputDigit("4")} className="h-16 text-xl rounded-full bg-neutral-700 text-white hover:bg-neutral-600 font-medium">4</Button>
            <Button onClick={() => inputDigit("5")} className="h-16 text-xl rounded-full bg-neutral-700 text-white hover:bg-neutral-600 font-medium">5</Button>
            <Button onClick={() => inputDigit("6")} className="h-16 text-xl rounded-full bg-neutral-700 text-white hover:bg-neutral-600 font-medium">6</Button>
            <OperatorButton op="-" label="−" />

            {/* Row 4 */}
            <Button onClick={() => inputDigit("1")} className="h-16 text-xl rounded-full bg-neutral-700 text-white hover:bg-neutral-600 font-medium">1</Button>
            <Button onClick={() => inputDigit("2")} className="h-16 text-xl rounded-full bg-neutral-700 text-white hover:bg-neutral-600 font-medium">2</Button>
            <Button onClick={() => inputDigit("3")} className="h-16 text-xl rounded-full bg-neutral-700 text-white hover:bg-neutral-600 font-medium">3</Button>
            <OperatorButton op="+" label="+" />

            {/* Row 5 */}
            <Button onClick={() => inputDigit("0")} className="h-16 text-xl rounded-full bg-neutral-700 text-white hover:bg-neutral-600 font-medium col-span-2">0</Button>
            <Button onClick={inputDecimal} className="h-16 text-xl rounded-full bg-neutral-700 text-white hover:bg-neutral-600 font-medium">.</Button>
            <Button onClick={handleEquals} className="h-16 text-xl rounded-full bg-orange-500 text-white hover:bg-orange-400 font-medium">=</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default App;
